import type { Locale } from '@ohear/shared'
/**
 * "My account" API surface.
 *
 * Backs profile + actor_profile editing, the participating-projects list, the
 * recent-sessions log, and the destructive data-export / delete-request
 * endpoints. All requests resolve to the currently authenticated user — the
 * server reads the JWT and never trusts a client-supplied user id.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

export interface AccountProfile {
  user_id: string
  email: string
  /** Legal name on contracts (immutable once a contract is signed). */
  legal_name: string | null
  /** Public stage / VO-credit name. Free to change. */
  stage_name: string | null
  /** IANA timezone, e.g. "America/Los_Angeles". */
  timezone: string | null
  avatar_url: string | null
  primary_role: 'owner' | 'member' | 'actor' | 'supervisor' | null
  // ── Basic PII (added in 0009 migration) ───────────────────────────────
  phone?: string | null
  phone_verified?: boolean
  phone_verified_at?: string | null
  profile_image_r2_key?: string | null
  preferred_contact_method?: 'email' | 'phone' | 'sms'
  marketing_opt_in?: boolean
  /** Filled only for actors. */
  actor_profile: ActorProfile | null
  /**
   * ISO timestamp the user last ran any of the device tests (mic /
   * headphone / network). Onboarding consults this to decide whether to
   * surface the "verify your studio" gate before the first record session.
   */
  last_device_test_at?: string | null
  /**
   * Per-section snapshot of the most recent test results. Each section
   * is independent: re-running just the network test only refreshes the
   * `network` key, leaving `mic` and `headphone` untouched.
   */
  last_device_test_results?: DeviceTestResultsSnapshot | null
}

export interface DeviceTestResultsSnapshot {
  mic?: {
    peakDbfs: number
    noiseFloorDbfs: number
    rmsDbfs?: number
    durationSec?: number
    testedAt: string
  }
  headphone?: {
    passed: boolean
    testedAt: string
  }
  network?: {
    medianPingMs: number
    uploadMbps: number
    testedAt: string
  }
}

export interface ActorProfile {
  /** Free-form blurb shown on the casting card. */
  voice_profile: string | null
  /** Public demo URL (SoundCloud, Vimeo, etc). */
  demo_url: string | null
  participating_projects: readonly ParticipatingProject[]
  // ── Performer particulars (added in 0009 migration) ───────────────────
  pronouns?: string | null
  bio?: string | null
  years_experience?: number | null
  languages?: readonly string[] | null
  accents?: readonly string[] | null
  union_status?: 'non_union' | 'sag_aftra' | 'actra' | 'equity' | 'other' | null
  union_id?: string | null
  rate_per_line_cents?: number | null
  willing_to_travel?: boolean
  home_studio_description?: string | null
}

export interface ParticipatingProject {
  project_id: string
  organization_slug: string
  organization_name: string
  project_name: string
  character_display_name: string
  status: 'in_progress' | 'completed' | 'paused'
}

export interface AccountProfileUpdate {
  legal_name?: string
  stage_name?: string
  timezone?: string
  voice_profile?: string
  demo_url?: string
  /** UI language preference (`en` or `zh`). Persisted on the user row, not
   *  the actor profile, so non-actor accounts can also set it. */
  locale?: Locale
}

export interface SessionRecord {
  id: string
  ip: string | null
  user_agent: string | null
  /** ISO-8601 timestamp. */
  signed_in_at: string
  /** True when this session is the one rendering the page. */
  current: boolean
}

export interface DataExportJob {
  job_id: string
  /** ISO-8601 of when the export will be ready (best estimate). */
  ready_eta: string
  /** Email the link will be sent to (always equals the account email). */
  email: string
}

export interface DeleteRequestResult {
  /** Hard delete is async; surfaced so the UI can show "we'll get back to you within Xh". */
  acknowledged: true
  /** Free-text reason recorded for the support team. */
  reason: string | null
}

const profileKey = ['account', 'profile'] as const
const sessionsKey = ['account', 'sessions'] as const

/** The signed-in user's profile + (for actors) actor_profile. */
export function useAccountProfile() {
  return useQuery({
    queryKey: profileKey,
    queryFn: () => api<AccountProfile>('/api/v1/me/profile'),
  })
}

/**
 * PATCH the editable subset of profile fields. The server enforces which
 * fields are read-only for the caller's role (e.g. legal_name locks once a
 * contract is signed).
 */
export function useUpdateAccountProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: AccountProfileUpdate) =>
      api<AccountProfile>('/api/v1/me/profile', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: (next) => {
      qc.setQueryData<AccountProfile>(profileKey, next)
    },
  })
}

/** Recent magic-link sessions for the security tab. */
export function useRecentSessions() {
  return useQuery({
    queryKey: sessionsKey,
    queryFn: () => api<{ sessions: readonly SessionRecord[] }>('/api/v1/me/sessions'),
  })
}

/** Sign-out everywhere (revoke other sessions). */
export function useRevokeOtherSessions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api<{ revoked: number }>('/api/v1/me/sessions/revoke-others', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionsKey })
    },
  })
}

/** Queue an async export; result emailed when ready. */
export function useRequestDataExport() {
  return useMutation({
    mutationFn: () => api<DataExportJob>('/api/v1/me/data-export', { method: 'POST' }),
  })
}

/**
 * Persist the user's UI locale preference to the server. Best-effort —
 * the language switcher invokes this without awaiting; localStorage is the
 * authoritative source on the current device. Server value is consulted on
 * fresh sign-in to seed the initial locale on a new device.
 */
export function useUpdateLocale() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (locale: Locale) =>
      api<AccountProfile>('/api/v1/me', {
        method: 'PATCH',
        body: JSON.stringify({ locale }),
      }),
    onSuccess: (next) => {
      qc.setQueryData<AccountProfile>(profileKey, next)
    },
  })
}

/**
 * Mic / headphone / network test results persisted on the user row. The
 * server merges by top-level section name, so callers can submit any
 * subset (e.g. just `{ mic: ... }` after the mic test finishes) without
 * clobbering the others.
 *
 * Mirror of the body shape accepted by `POST /api/v1/me/device-test-results`.
 */
export interface DeviceTestResultsPayload {
  mic?: {
    peakDbfs: number
    noiseFloorDbfs: number
    rmsDbfs?: number
    durationSec?: number
    testedAt: string
  }
  headphone?: {
    passed: boolean
    testedAt: string
  }
  network?: {
    medianPingMs: number
    uploadMbps: number
    testedAt: string
  }
}

export interface DeviceTestResultsResponse {
  ok: true
  /** ISO timestamp the server stamped onto users.last_device_test_at. */
  saved_at: string
}

/**
 * Persist a partial set of device-test results. Each individual test
 * (MicTest / HeadphoneTest / NetworkTest) calls this with just its own
 * section as soon as it finishes; the server merges the JSONB blob.
 *
 * On success we invalidate the profile query so any consumer reading the
 * `last_device_test_at` field (e.g. the onboarding "is your studio ready?"
 * gate) gets a fresh value on the next render.
 */
export function useSaveDeviceTestResults() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (results: DeviceTestResultsPayload) =>
      api<DeviceTestResultsResponse>('/api/v1/me/device-test-results', {
        method: 'POST',
        body: JSON.stringify(results),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profileKey })
    },
  })
}

/** Submit a delete-account request. Honored by the support team. */
export function useRequestAccountDelete() {
  return useMutation({
    mutationFn: (reason: string | null) =>
      api<DeleteRequestResult>('/api/v1/me/delete-request', {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
  })
}

// ─── Basic PII (added via 0009 migration) ──────────────────────────────────
//
// These hooks back the new account-settings fields: phone (with verify
// flow), preferred contact method, marketing opt-in, profile image, and
// the actor performer particulars (pronouns, bio, languages, accents,
// union, rate, travel, home studio).

export type ContactMethod = 'email' | 'phone' | 'sms'
export type UnionStatusCode = 'non_union' | 'sag_aftra' | 'actra' | 'equity' | 'other'

/** Patch body for the narrow `PATCH /me` user-row update. */
export interface MePatch {
  phone?: string | null
  preferred_contact_method?: ContactMethod
  marketing_opt_in?: boolean
  profile_image_r2_key?: string | null
}

/** Patch body for `PATCH /me/actor-profile` (performer particulars). */
export interface ActorProfilePatch {
  pronouns?: string | null
  bio?: string | null
  years_experience?: number | null
  languages?: string[]
  accents?: string[]
  union_status?: UnionStatusCode | null
  union_id?: string | null
  rate_per_line_cents?: number | null
  willing_to_travel?: boolean
  home_studio_description?: string | null
}

/** Response from `POST /me/profile-image/upload` — gives the client what
 *  it needs to PUT directly to R2 and then commit the key via PATCH /me. */
export interface ProfileImageUploadHandle {
  r2_key: string
  upload_url: string
  method: 'PUT'
  content_type: 'image/jpeg' | 'image/png'
  max_bytes: number
  expires_in: number
}

/** Narrow `PATCH /me` mutation — invalidates the profile cache on success. */
export function useUpdateMe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: MePatch) =>
      api<{ ok: true }>('/api/v1/me', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profileKey })
    },
  })
}

/** Actor-only update to the performer particulars block. */
export function useUpdateActorProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: ActorProfilePatch) =>
      api<{ ok: true }>('/api/v1/me/actor-profile', {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profileKey })
    },
  })
}

/** Kick off the SMS OTP send (V1: dev-stub, accepts any number on file). */
export function useStartPhoneVerification() {
  return useMutation({
    mutationFn: () =>
      api<{ ok: true; expires_in: number }>('/api/v1/me/phone/send-code', {
        method: 'POST',
        body: JSON.stringify({}),
      }),
  })
}

/** Submit the 6-digit code (V1 dev-stub accepts '000000'). */
export function useVerifyPhoneCode() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      api<{ ok: true; phone_verified_at: string }>('/api/v1/me/phone/verify', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: profileKey })
    },
  })
}

/**
 * Two-step profile-image upload:
 *   1. POST /me/profile-image/upload → presigned URL + r2_key
 *   2. PUT bytes directly to R2
 *   3. PATCH /me { profile_image_r2_key } via `useUpdateMe`
 *
 * Returns the new r2_key on success so the caller can immediately commit.
 */
export function useUploadProfileImage() {
  return useMutation({
    mutationFn: async (file: File): Promise<string> => {
      const contentType: 'image/jpeg' | 'image/png' =
        file.type === 'image/png' ? 'image/png' : 'image/jpeg'

      const handle = await api<ProfileImageUploadHandle>('/api/v1/me/profile-image/upload', {
        method: 'POST',
        body: JSON.stringify({ content_type: contentType, size_bytes: file.size }),
      })

      // PUT bytes directly to R2 — bypasses the API rate limit and means
      // the file never traverses our own ingress. The presigned URL has
      // the content_type baked in; we must send the exact same header.
      const putRes = await fetch(handle.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': handle.content_type },
        body: file,
      })
      if (!putRes.ok) {
        throw new Error(`Profile image upload failed (${putRes.status})`)
      }
      return handle.r2_key
    },
  })
}

/**
 * Lazy fetch of the signed download URL for the current user's profile
 * image. Returns `null` (and disables the query) when the user has no
 * image. The URL is short-lived (10 min by default) — the consumer
 * should let React Query refetch on mount rather than caching forever.
 */
export function useProfileImageUrl(r2Key: string | null | undefined) {
  return useQuery({
    queryKey: ['account', 'profile-image-url', r2Key],
    enabled: Boolean(r2Key),
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      api<{ url: string; expires_in: number }>('/api/v1/me/profile-image/url').then((r) => r.url),
  })
}
